import React, { useEffect, useState } from "react";
import { User } from "oidc-client-ts";
import { orendaOidcConfig } from "../../config/orendaOidcConfig";
import { orendaUserManager } from "./orendaUserManager";
import { saveOrendaAuth } from "./orendaAuthStorage";

// Cognito redirects here after successful sign-in with ?code=...&state=...
// We exchange the code for tokens, hand the User object to the UserManager
// (so silent renewal starts), and forward the user on to /orenda-landing.
// The clean UX is a spinner + one line of copy. Add ?debug=1 to reveal the
// full diagnostic dump — useful when we're debugging shape mismatches.

const HOSTED_UI = "https://bopayments.auth.eu-west-1.amazoncognito.com";
const TOKEN_URL = `${HOSTED_UI}/oauth2/token`;

const decodeJwt = (token) => {
  if (!token) return null;
  try {
    const payload = token.split(".")[1];
    const normalized = payload.replace(/-/g, "+").replace(/_/g, "/");
    return JSON.parse(atob(normalized));
  } catch (e) {
    return { _decodeError: e.message };
  }
};

const parseQuery = (search) => {
  const out = {};
  new URLSearchParams(search).forEach((v, k) => (out[k] = v));
  return out;
};

const OrendaCallback = () => {
  const [message, setMessage] = useState("Finishing sign-in…");
  const [error, setError] = useState(null);
  const [debug, setDebug] = useState(null); // Only populated when ?debug=1

  useEffect(() => {
    const wantsDebug =
      new URLSearchParams(window.location.search).get("debug") === "1";

    const run = async () => {
      const url = window.location.href;
      const query = parseQuery(window.location.search);
      const stored = window.sessionStorage.getItem(
        `oidc.${query.state || ""}`
      );

      if (query.error) {
        setError(
          query.error_description ||
            `Sign-in failed (${query.error}).`
        );
        return;
      }
      if (!query.code) {
        setError("Sign-in failed. Please try again.");
        return;
      }

      let code_verifier = null;
      try {
        const entry = JSON.parse(stored || "{}");
        code_verifier = entry.code_verifier;
      } catch {
        // ignore
      }
      if (!code_verifier) {
        setError(
          "Sign-in failed. Please close this tab and try again from the beginning."
        );
        return;
      }

      const body = new URLSearchParams({
        grant_type: "authorization_code",
        client_id: orendaOidcConfig.client_id,
        code: query.code,
        redirect_uri: orendaOidcConfig.redirect_uri,
        code_verifier,
      });

      try {
        const res = await fetch(TOKEN_URL, {
          method: "POST",
          headers: { "Content-Type": "application/x-www-form-urlencoded" },
          body: body.toString(),
        });
        const text = await res.text();
        let json = null;
        try { json = JSON.parse(text); } catch { /* raw text */ }

        if (wantsDebug) {
          setDebug({
            url,
            query,
            storedState: stored,
            tokenRequest: {
              method: "POST",
              url: TOKEN_URL,
              body: body.toString(),
            },
            httpStatus: res.status,
            rawResponseText: text,
            tokenResponse: json,
          });
        }

        if (!res.ok || !json?.id_token) {
          setError("Sign-in failed. Please try again.");
          return;
        }

        // Persist tokens and hand the User to oidc-client-ts so silent renewal
        // starts.
        saveOrendaAuth(json);
        try {
          const profile = decodeJwt(json.id_token) || {};
          const nowSec = Math.floor(Date.now() / 1000);
          const user = new User({
            id_token: json.id_token,
            access_token: json.access_token,
            refresh_token: json.refresh_token,
            token_type: json.token_type || "Bearer",
            scope: json.scope,
            profile,
            expires_at: profile.exp || nowSec + (json.expires_in || 300),
          });
          await orendaUserManager.storeUser(user);
        } catch (storeErr) {
          // eslint-disable-next-line no-console
          console.warn("[orenda] storeUser failed:", storeErr?.message || storeErr);
        }

        if (wantsDebug) {
          setMessage("Sign-in complete. (Debug mode — auto-redirect disabled.)");
          return;
        }
        setMessage("Signed in. Redirecting…");
        window.setTimeout(() => window.location.replace("/orenda-landing"), 250);
      } catch (e) {
        setError("Sign-in failed. Please try again.");
      }
    };
    run();
  }, []);

  if (error) {
    return (
      <div className="col-lg-9 row minvh-100" style={{ padding: "60px 40px" }}>
        <div className="col-12">
          <h2 className="orenda-heading">Sign-in problem</h2>
          <div className="orenda-alert orenda-alert--warn">{error}</div>
          <a href="/orenda-landing" className="orenda-btn-primary">
            Try again
          </a>
        </div>
      </div>
    );
  }

  return (
    <div
      className="col-lg-9 row minvh-100"
      style={{ padding: "60px 40px", alignContent: "center" }}
    >
      <div className="col-12" style={{ textAlign: "center" }}>
        <h2 className="orenda-heading" style={{ margin: "0 auto" }}>
          {message}
        </h2>
        {debug && <DebugPanel debug={debug} />}
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────
// Debug panel (only when ?debug=1). Kept for future protocol-level troubleshooting.
// ─────────────────────────────────────────────────────────────────
const DebugPanel = ({ debug }) => {
  const idClaims = debug.tokenResponse?.id_token
    ? decodeJwt(debug.tokenResponse.id_token)
    : null;
  const pre = {
    background: "#ecebe4",
    padding: 12,
    fontSize: 12,
    fontFamily: "ui-monospace, monospace",
    maxHeight: 320,
    overflow: "auto",
    textAlign: "left",
    whiteSpace: "pre-wrap",
    wordBreak: "break-all",
  };
  return (
    <div style={{ marginTop: 32, textAlign: "left", maxWidth: 900, margin: "32px auto 0" }}>
      <div className="orenda-eyebrow">Debug · sign-in flow</div>
      <pre style={pre}>{JSON.stringify(debug, null, 2)}</pre>
      <div className="orenda-eyebrow" style={{ marginTop: 12 }}>Decoded id_token claims</div>
      <pre style={pre}>{JSON.stringify(idClaims, null, 2)}</pre>
    </div>
  );
};

export default OrendaCallback;
